export interface LayoutNode {
  id: string;
  radius: number;
}

export interface LayoutLink {
  source: string | { id: string };
  target: string | { id: string };
}

export interface LayoutOptions {
  /** Vertical distance between two consecutive layers. */
  rowGap?: number;
  /** Horizontal breathing room added to each node's diameter. */
  nodeGap?: number;
  /** Barycentre passes used to reduce edge crossings. */
  sweeps?: number;
  /** A row wider than this is stacked into several bands instead of one line. */
  maxRowWidth?: number;
  /** Widest a parent's block of leaf children may get before it wraps. */
  bundleColumns?: number;
}

export interface LayoutPosition {
  x: number;
  y: number;
}

const endpointId = (endpoint: string | { id: string }) =>
  typeof endpoint === 'string' ? endpoint : endpoint.id;

/**
 * Drops the edges that close a cycle so the rest can be layered as a DAG.
 * Iterative DFS: an edge pointing at a node still on the stack is a back edge.
 */
function removeBackEdges(nodeCount: number, edges: Array<[number, number]>) {
  const outgoing: number[][] = Array.from({ length: nodeCount }, () => []);
  edges.forEach(([source], i) => outgoing[source].push(i));

  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Uint8Array(nodeCount);
  const dropped = new Set<number>();

  for (let root = 0; root < nodeCount; root++) {
    if (colour[root] !== WHITE) continue;
    colour[root] = GREY;
    const stack = [{ node: root, next: 0 }];
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      if (frame.next >= outgoing[frame.node].length) {
        colour[frame.node] = BLACK;
        stack.pop();
        continue;
      }
      const edgeIndex = outgoing[frame.node][frame.next++];
      const target = edges[edgeIndex][1];
      if (colour[target] === GREY) {
        dropped.add(edgeIndex);
      } else if (colour[target] === WHITE) {
        colour[target] = GREY;
        stack.push({ node: target, next: 0 });
      }
    }
  }

  return edges.filter((_, i) => !dropped.has(i));
}

/** Longest-path layering: every node sits one row below its deepest parent. */
function assignLayers(nodeCount: number, edges: Array<[number, number]>) {
  const inDegree = new Int32Array(nodeCount);
  const outgoing: number[][] = Array.from({ length: nodeCount }, () => []);
  for (const [source, target] of edges) {
    outgoing[source].push(target);
    inDegree[target]++;
  }

  const layer = new Int32Array(nodeCount);
  const queue: number[] = [];
  for (let i = 0; i < nodeCount; i++) if (inDegree[i] === 0) queue.push(i);

  for (let head = 0; head < queue.length; head++) {
    const node = queue[head];
    for (const target of outgoing[node]) {
      if (layer[target] < layer[node] + 1) layer[target] = layer[node] + 1;
      if (--inDegree[target] === 0) queue.push(target);
    }
  }

  return layer;
}

/**
 * Lays nodes out in top-down rows, the way someone would arrange them by hand:
 * roots on top, each node below its deepest parent, siblings side by side.
 *
 * Ordering within a row uses the classic barycentre heuristic — repeatedly move
 * each node next to the average position of its neighbours in the adjacent row —
 * which is what keeps edges from turning into a cross-hatch.
 *
 * Leaf children belonging to a single parent are pulled out of their layer and
 * drawn as a block underneath that parent. Left in the layer they would spread
 * across the full width of the graph, metres away from the node they belong to.
 *
 * Whatever is left in a layer is capped at `maxRowWidth` and stacked into bands
 * rather than running off as one endless line.
 */
export function hierarchicalLayout(
  nodes: LayoutNode[],
  links: LayoutLink[],
  options: LayoutOptions = {},
): Map<string, LayoutPosition> {
  const {
    rowGap = 170,
    nodeGap = 44,
    sweeps = 6,
    maxRowWidth = 6000,
    bundleColumns = 3,
  } = options;
  const positions = new Map<string, LayoutPosition>();
  if (nodes.length === 0) return positions;

  const indexById = new Map(nodes.map((node, i) => [node.id, i]));
  const edges: Array<[number, number]> = [];
  const seen = new Set<string>();
  for (const link of links) {
    const source = indexById.get(endpointId(link.source));
    const target = indexById.get(endpointId(link.target));
    if (source === undefined || target === undefined || source === target) continue;
    const key = `${source}->${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push([source, target]);
  }

  const dag = removeBackEdges(nodes.length, edges);
  const layer = assignLayers(nodes.length, dag);

  const predecessors: number[][] = Array.from({ length: nodes.length }, () => []);
  const successors: number[][] = Array.from({ length: nodes.length }, () => []);
  for (const [source, target] of dag) {
    successors[source].push(target);
    predecessors[target].push(source);
  }

  const width = (node: number) => nodes[node].radius * 2 + nodeGap;

  // A node belongs to its parent's block only if nothing else claims it: one
  // parent, no children of its own. Anything shared stays in the layered graph.
  const bundled = new Uint8Array(nodes.length);
  const bundles = new Map<number, number[]>();
  for (let i = 0; i < nodes.length; i++) {
    if (successors[i].length > 0 || predecessors[i].length !== 1) continue;
    bundled[i] = 1;
    const parent = predecessors[i][0];
    const members = bundles.get(parent);
    if (members) members.push(i);
    else bundles.set(parent, [i]);
  }

  const grids = new Map<number, { lines: number[][]; width: number }>();
  for (const [parent, members] of bundles) {
    const columns = Math.min(bundleColumns, Math.ceil(Math.sqrt(members.length)));
    const lines: number[][] = [];
    for (let i = 0; i < members.length; i += columns) {
      lines.push(members.slice(i, i + columns));
    }
    const lineWidth = (line: number[]) =>
      line.reduce((sum, node) => sum + width(node), 0);
    grids.set(parent, {
      lines,
      width: Math.max(...lines.map(lineWidth)),
    });
  }

  /** How much horizontal room a node needs once its block is accounted for. */
  const footprint = (node: number) =>
    Math.max(width(node), grids.get(node)?.width ?? 0);

  const rowCount = nodes.reduce(
    (max, _, i) => (bundled[i] ? max : Math.max(max, layer[i])),
    0,
  ) + 1;
  const rows: number[][] = Array.from({ length: rowCount }, () => []);
  nodes.forEach((_, i) => {
    if (!bundled[i]) rows[layer[i]].push(i);
  });

  const order = new Float64Array(nodes.length);
  const reindex = () => rows.forEach(row => row.forEach((node, i) => (order[node] = i)));
  reindex();

  const barycentre = (node: number, neighbours: number[][]) => {
    const group = neighbours[node].filter(n => !bundled[n]);
    if (group.length === 0) return order[node];
    return group.reduce((sum, n) => sum + order[n], 0) / group.length;
  };

  for (let pass = 0; pass < sweeps; pass++) {
    const downward = pass % 2 === 0;
    const neighbours = downward ? predecessors : successors;
    const sequence = downward
      ? rows.map((_, i) => i)
      : rows.map((_, i) => rowCount - 1 - i);
    for (const rowIndex of sequence) {
      const keys = new Map(rows[rowIndex].map(n => [n, barycentre(n, neighbours)]));
      rows[rowIndex].sort((a, b) => (keys.get(a) as number) - (keys.get(b) as number));
      reindex();
    }
  }

  // Bands preserve the row order, so neighbouring nodes stay contiguous even
  // when the row they are in spills onto the next line.
  const bandsOf = (row: number[]) => {
    const bands: number[][] = [];
    let used = Infinity;
    for (const node of row) {
      const nodeWidth = footprint(node);
      if (bands.length === 0 || used + nodeWidth > maxRowWidth) {
        bands.push([node]);
        used = nodeWidth;
      } else {
        bands[bands.length - 1].push(node);
        used += nodeWidth;
      }
    }
    return bands;
  };

  const placeBundle = (parent: number, parentX: number, parentY: number) => {
    const grid = grids.get(parent);
    if (!grid) return 0;
    grid.lines.forEach((line, lineIndex) => {
      const total = line.reduce((sum, node) => sum + width(node), 0);
      let cursor = parentX - total / 2;
      for (const node of line) {
        positions.set(nodes[node].id, {
          x: cursor + width(node) / 2,
          y: parentY + (lineIndex + 1) * rowGap,
        });
        cursor += width(node);
      }
    });
    return grid.lines.length;
  };

  let y = 0;
  for (const row of rows) {
    if (row.length === 0) continue;
    for (const band of bandsOf(row)) {
      const total = band.reduce((sum, node) => sum + footprint(node), 0);
      let cursor = -total / 2;
      let deepest = 0;
      for (const node of band) {
        const x = cursor + footprint(node) / 2;
        positions.set(nodes[node].id, { x, y });
        deepest = Math.max(deepest, placeBundle(node, x, y));
        cursor += footprint(node);
      }
      // Clear the tallest block hanging off this band before the next one.
      y += rowGap * (1 + deepest);
    }
  }

  return positions;
}
